import { istToday, istYearMonth } from './formatIst';

const IST = 'Asia/Kolkata';

export const ORDER_PERIOD_TABS = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'last_week', label: 'Last week' },
  { id: 'month', label: 'This month' },
  { id: 'quarter', label: 'This quarter' },
  { id: 'year', label: 'This year' },
  { id: 'custom', label: 'Custom range' },
];

/** Parse order_date to YYYY-MM-DD (wall date). */
export function parseOrderDateYmd(orderDate) {
  const raw = String(orderDate || '').trim().slice(0, 10);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

function addDaysYmd(ymd, deltaDays) {
  const dt = new Date(`${ymd}T12:00:00+05:30`);
  dt.setTime(dt.getTime() + deltaDays * 86400000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: IST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(dt);
}

/** Monday of the week containing `ymd` (IST, week starts Monday). */
function mondayOfWeekYmd(ymd) {
  const dt = new Date(`${ymd}T12:00:00+05:30`);
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: IST,
    weekday: 'short',
  }).format(dt);
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dayIndex = map[weekday] ?? 0;
  const daysFromMonday = (dayIndex + 6) % 7;
  return addDaysYmd(ymd, -daysFromMonday);
}

/** Normalize custom range; swaps start/end if reversed. */
export function normalizeCustomRange(customRange) {
  const start = parseOrderDateYmd(customRange?.start);
  const end = parseOrderDateYmd(customRange?.end);
  if (!start || !end) return null;
  if (start <= end) return { start, end };
  return { start: end, end: start };
}

/**
 * Inclusive date range { start, end } as YYYY-MM-DD in IST, or null for "all".
 * For periodId `custom`, pass customRange { start, end } (YYYY-MM-DD).
 */
export function getIstPeriodBounds(periodId, customRange = null) {
  if (!periodId || periodId === 'all') return null;

  if (periodId === 'custom') {
    return normalizeCustomRange(customRange);
  }

  const end = istToday();
  const [y, m] = end.split('-').map((n) => parseInt(n, 10));

  if (periodId === 'today') {
    return { start: end, end };
  }
  if (periodId === 'week') {
    return { start: mondayOfWeekYmd(end), end };
  }
  if (periodId === 'last_week') {
    const thisMonday = mondayOfWeekYmd(end);
    const lastSunday = addDaysYmd(thisMonday, -1);
    const lastMonday = addDaysYmd(thisMonday, -7);
    return { start: lastMonday, end: lastSunday };
  }
  if (periodId === 'month') {
    return { start: `${y}-${String(m).padStart(2, '0')}-01`, end };
  }
  if (periodId === 'quarter') {
    const qStartMonth = Math.floor((m - 1) / 3) * 3 + 1;
    return { start: `${y}-${String(qStartMonth).padStart(2, '0')}-01`, end };
  }
  if (periodId === 'year') {
    return { start: `${y}-01-01`, end };
  }
  return null;
}

export function orderDateInYmdRange(orderDate, bounds) {
  if (!bounds) return true;
  const key = parseOrderDateYmd(orderDate);
  if (!key) return false;
  return key >= bounds.start && key <= bounds.end;
}

/** True if order_date falls within the IST period (by order date). */
export function orderDateInPeriod(orderDate, periodId, customRange = null) {
  if (!periodId || periodId === 'all') return true;
  const bounds = getIstPeriodBounds(periodId, customRange);
  return orderDateInYmdRange(orderDate, bounds);
}

/** Default custom range: start of current month → today (IST). */
export function defaultCustomRange() {
  return { start: `${istYearMonth()}-01`, end: istToday() };
}
